import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { apiClient } from '@/services/apiClient'
import type {
  ReviewKind, ReviewResult, ReviewStateResponse, ReviewStatus,
} from '@/types/personaReview'

export const personaReviewKey = (personaId: string) => ['persona-review', personaId]
export const reviewStatusKey = (personaId: string) => ['persona-review', 'status', personaId]

/**
 * Cached results + server-computed staleness. Runs no LLM, so it is safe
 * to fetch on mount. Keyed on personaId (not bundleId) because the row
 * survives saves — staleness is what tells the user it's out of date.
 */
export function usePersonaReview(personaId: string | null) {
  return useQuery<ReviewStateResponse, Error>({
    queryKey: personaReviewKey(personaId ?? ''),
    queryFn: () => apiClient.get<ReviewStateResponse>(`/api/personas/${personaId}/review`),
    enabled: personaId !== null,
    refetchOnWindowFocus: false,
    retry: false,
  })
}

export function useReviewStatus(personaId: string | null) {
  return useQuery<ReviewStatus, Error>({
    queryKey: reviewStatusKey(personaId ?? ''),
    queryFn: () => apiClient.get<ReviewStatus>(`/api/personas/${personaId}/review/status`),
    enabled: personaId !== null,
    staleTime: 60_000,
    retry: false,
  })
}

/** Runs one kind. Costs money and latency — only ever call from an
 *  explicit user action, never on mount or on save. */
export function useRunReview(personaId: string | null) {
  const qc = useQueryClient()
  return useMutation<ReviewResult, Error, ReviewKind>({
    mutationFn: (kind) =>
      apiClient.post<ReviewResult>(`/api/personas/${personaId}/review`, { kind }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: personaReviewKey(personaId ?? '') })
    },
  })
}
